<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>
          <v-alert v-if="success" type="success" class="mb-4" closable @click:close="success = ''">
            {{ success }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="obra-form-panel">
            <div class="obra-form-header">
              <v-icon size="22" color="#ffffff">mdi-domain</v-icon>
              <span>{{ headerTitle }}</span>
            </div>

            <div class="obra-form-body">
              <div class="profile-layout">
                <!-- Navegación lateral de pestañas -->
                <nav class="profile-tabs">
                  <button
                    type="button"
                    class="profile-tab"
                    :class="{ 'profile-tab-active': activeTab === 'basic' }"
                    @click="activeTab = 'basic'"
                  >DATOS BÁSICOS</button>
                  <button
                    type="button"
                    class="profile-tab"
                    :class="{ 'profile-tab-active': activeTab === 'legal' }"
                    @click="activeTab = 'legal'"
                  >DATOS LEGALES</button>
                  <button
                    v-if="showInterests"
                    type="button"
                    class="profile-tab"
                    :class="{ 'profile-tab-active': activeTab === 'interests' }"
                    @click="activeTab = 'interests'"
                  >MIS INTERESES</button>
                </nav>

                <div class="profile-content">
                  <!-- ===== DATOS BÁSICOS ===== -->
                  <div v-show="activeTab === 'basic'">
                    <v-row>
                      <!-- Cliente: nombre y apellido de su cuenta, los mismos del
                           contrato. Se ingresan al registrarse y no los edita. -->
                      <template v-if="showInterests">
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="(me && me.firstName) || ''"
                            label="Nombre"
                            variant="outlined"
                            readonly
                            disabled
                            hint="Para cambiarlo, contacta a la administración."
                            persistent-hint
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="(me && me.lastName) || ''"
                            label="Apellido"
                            variant="outlined"
                            readonly
                            disabled
                            hint="Para cambiarlo, contacta a la administración."
                            persistent-hint
                          />
                        </v-col>
                      </template>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="company.name"
                          :label="showInterests ? 'Nombre de la empresa' : 'Nombre de fantasía'"
                          variant="outlined"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="company.email" label="Email de contacto" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="company.phone" label="Teléfono" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-text-field v-model="company.address" label="Dirección" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="company.city" label="Ciudad / Comuna" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="company.region" label="Región" variant="outlined" />
                      </v-col>
                      <v-col cols="12">
                        <v-textarea v-model="company.description" label="Descripción" rows="3" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="company.website" label="Sitio web" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="company.logo"
                          label="URL del logo"
                          variant="outlined"
                          hint="Pega una URL o sube un archivo (JPG/PNG, 500x500 px)"
                          persistent-hint
                        />
                        <div class="logo-upload-row">
                          <img
                            v-if="logoPreview"
                            :src="logoPreview"
                            class="logo-preview"
                            alt="Logotipo de la compañía"
                          />
                          <input
                            ref="logoInput"
                            type="file"
                            accept="image/*"
                            class="logo-file-hidden"
                            @change="onLogoSelected"
                          />
                          <button type="button" class="logo-upload-btn" @click="triggerLogoPicker">
                            <v-icon size="16">mdi-image-outline</v-icon>
                            SUBIR LOGOTIPO
                          </button>
                          <span v-if="logoFile" class="logo-file-name">{{ logoFile.name }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="company.facebook" label="Facebook" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="company.instagram" label="Instagram" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="company.tiktok" label="TikTok" variant="outlined" />
                      </v-col>
                      <!-- Cliente: mismos géneros de interés que la pestaña
                           "Mis intereses" (segmentan las campañas). -->
                      <v-col v-if="showInterests" cols="12" md="6">
                        <v-select
                          v-model="interests.genreIds"
                          :items="genres"
                          item-title="name"
                          item-value="id"
                          label="Intereses"
                          multiple
                          chips
                          variant="outlined"
                        />
                      </v-col>
                      <v-col v-else cols="12" md="6">
                        <v-select
                          v-model="company.interest_ids"
                          :items="tags"
                          item-title="name"
                          item-value="id"
                          label="Intereses"
                          multiple
                          chips
                          variant="outlined"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="company.client_type"
                          :items="clientTypeOptions"
                          item-title="label"
                          item-value="value"
                          label="Tipo de cliente"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                    </v-row>
                  </div>

                  <!-- ===== DATOS LEGALES ===== -->
                  <div v-show="activeTab === 'legal'">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="company.social_name" label="Razón social" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field v-model="company.rut" label="RUT" variant="outlined" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-select
                          v-model="company.productora"
                          :items="productoras"
                          item-title="name"
                          item-value="id"
                          label="Productora"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                    </v-row>

                    <div class="reps-section">
                      <div class="reps-title">Representantes legales</div>

                      <div
                        v-for="(rep, idx) in company.legal_representatives"
                        :key="idx"
                        class="reps-row"
                      >
                        <v-row>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="rep.first_name" label="Nombres" variant="outlined" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="rep.last_name" label="Apellidos" variant="outlined" />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field v-model="rep.rut" label="RUT" variant="outlined" />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field v-model="rep.phone" label="Teléfono" variant="outlined" />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field v-model="rep.email" label="Email" variant="outlined" />
                          </v-col>
                        </v-row>
                        <div class="reps-row-footer">
                          <v-switch
                            v-model="rep.is_primary"
                            label="Principal"
                            color="primary"
                            hide-details
                            density="compact"
                          />
                          <button type="button" class="reps-remove" @click="removeRep(idx)">
                            <v-icon size="16">mdi-delete-outline</v-icon>
                            Quitar
                          </button>
                        </div>
                      </div>

                      <button type="button" class="reps-add" @click="addRep">
                        <v-icon size="16">mdi-plus</v-icon>
                        AGREGAR REPRESENTANTE
                      </button>
                    </div>
                  </div>

                  <!-- ===== MIS INTERESES (solo clientes) ===== -->
                  <div v-if="showInterests" v-show="activeTab === 'interests'" class="mi-section">
                    <h3 class="mi-heading">GÉNEROS DE INTERÉS</h3>
                    <div class="mi-genres">
                      <div v-for="g in genres" :key="g.id" class="mi-genre-row">
                        <span class="mi-genre-label">{{ g.name }}</span>
                        <v-switch
                          :model-value="interests.genreIds.includes(Number(g.id))"
                          color="red-darken-2"
                          hide-details
                          density="compact"
                          @update:model-value="toggleGenre(g.id, $event)"
                        />
                      </div>
                      <div v-if="genres.length === 0" class="mi-empty">No hay géneros disponibles.</div>
                    </div>

                    <h3 class="mi-heading mi-heading-mt">TEMPORADA</h3>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="interests.preferredSeason"
                          :items="seasonOptions"
                          item-title="label"
                          item-value="value"
                          label="Época del año preferida para funciones"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                    </v-row>

                    <h3 class="mi-heading mi-heading-mt">RANGO ETÁREO</h3>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="interests.ageRange"
                          :items="ageRangeOptions"
                          item-title="label"
                          item-value="value"
                          label="Público al que apuntas"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                    </v-row>

                    <h3 class="mi-heading mi-heading-mt">ESCENARIO</h3>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="interests.stageSurface"
                          :items="stageOptions"
                          item-title="label"
                          item-value="value"
                          label="Superficie del escenario (mts)"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                    </v-row>

                    <h3 class="mi-heading mi-heading-mt">PRESUPUESTO</h3>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="interests.budget"
                          type="number"
                          min="0"
                          label="Presupuesto para contratar una función"
                          placeholder="Escribe el valor máximo"
                          variant="outlined"
                        />
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Acciones -->
                  <div v-if="activeTab !== 'interests'" class="obra-form-actions">
                    <button type="button" class="obra-cancel-btn" @click="cancel">
                      CANCELAR Y VOLVER
                    </button>
                    <button type="button" class="obra-save-btn" :disabled="saving" @click="save">
                      {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                    </button>
                  </div>
                  <div v-else class="obra-form-actions obra-form-actions-center">
                    <button type="button" class="obra-save-btn" :disabled="savingInterests" @click="saveInterests">
                      {{ savingInterests ? 'GUARDANDO…' : 'ACTUALIZAR DATOS' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchCompanies,
  fetchCompany,
  fetchTags,
  fetchProductoras,
  fetchGenres,
  fetchMe,
  saveCompany,
  createCompany,
  uploadCompanyLogo,
  saveMyInterests,
  fetchClientTypes,
} from "@/services/teatroApi";

const STAGE_OPTIONS = [
  { value: "none", label: "Sin escenario" },
  { value: "6_10", label: "De 6 a 10 mts" },
  { value: "10_15", label: "De 10 a 15 m²" },
  { value: "15_plus", label: "Más de 15 m²" },
  { value: "open", label: "Escenario abierto" },
];

// Espejo de teatroapp.models.Season (segmenta los envíos de campañas).
const SEASON_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "summer", label: "Verano (dic - feb)" },
  { value: "autumn", label: "Otoño (mar - may)" },
  { value: "winter", label: "Invierno (jun - ago)" },
  { value: "spring", label: "Primavera (sep - nov)" },
  { value: "winter_break", label: "Vacaciones de invierno (julio)" },
  { value: "fiestas_patrias", label: "Fiestas Patrias (15 al 24 sept)" },
  { value: "christmas", label: "Navidad y Año Nuevo (dic - ene)" },
];

// Espejo de teatroapp.models.AgeRange.
const AGE_RANGE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "kids", label: "Infantil (0 a 12)" },
  { value: "teens", label: "Adolescentes (13 a 17)" },
  { value: "adults", label: "Adultos (18 a 59)" },
  { value: "seniors", label: "Adulto mayor (60+)" },
];


export default {
  name: "CompanyProfile",
  props: {
    // When set (admin editing a specific company), load that company.
    companyId: {
      type: [String, Number],
      default: null,
    },
    // When true, render an empty form to create a new company.
    create: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      company: { interest_ids: [] },
      logoFile: null,
      logoObjectUrl: "", // preview local del archivo recién elegido
      tags: [],
      productoras: [],
      genres: [],
      me: null,
      activeTab: "basic",
      stageOptions: STAGE_OPTIONS,
      seasonOptions: SEASON_OPTIONS,
      ageRangeOptions: AGE_RANGE_OPTIONS,
      clientTypeOptions: [],
      interests: {
        genreIds: [],
        stageSurface: "",
        budget: null,
        preferredSeason: "",
        ageRange: "",
      },
      loading: true,
      saving: false,
      savingInterests: false,
      error: "",
      success: "",
    };
  },
  computed: {
    isAdminFlow() {
      return this.create || this.companyId !== null;
    },
    isClient() {
      const role = this.me?.role || this.$store?.state?.role;
      return role === "client";
    },
    // La pestaña "Mis intereses" solo aplica al propio perfil de un cliente.
    showInterests() {
      return this.isClient && !this.isAdminFlow;
    },
    // Muestra el archivo recién elegido; si no hay, el logo ya guardado.
    logoPreview() {
      return this.logoObjectUrl || this.company.logo || "";
    },
    headerTitle() {
      if (this.create) return "NUEVA COMPAÑÍA";
      if (this.companyId) return "EDITAR COMPAÑÍA";
      if (this.isClient) return "MI PERFIL";
      return "DATOS DE LA COMPAÑÍA";
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  beforeUnmount() {
    this.clearLogoObjectUrl();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const tagsPromise = fetchTags().catch(() => []);
        const clientTypesPromise = fetchClientTypes().catch((e) => {
          console.error("No se pudieron cargar los rubros", e);
          return [];
        });
        // No es fatal: si falla, la compañía se sigue mostrando sin productoras.
        const productorasPromise = fetchProductoras().catch((e) => {
          console.error("No se pudieron cargar las productoras", e);
          return [];
        });

        if (this.create) {
          this.company = { interest_ids: [] };
        } else if (this.companyId) {
          this.company = (await fetchCompany(this.companyId)) || { interest_ids: [] };
        } else {
          const companies = await fetchCompanies();
          this.company = companies[0] || { interest_ids: [] };
        }
        this.company.interest_ids = (this.company.interest_ids || []).map(Number);
        this.company.legal_representatives = this.company.legal_representatives || [];
        this.tags = (await tagsPromise) || [];
        this.clientTypeOptions = (await clientTypesPromise) || [];
        this.productoras = (await productorasPromise) || [];
        // Keep the currently assigned productora selectable even if it is
        // inactive (managers only receive active ones from the API).
        if (
          this.company.productora &&
          !this.productoras.some((p) => p.id === this.company.productora)
        ) {
          this.productoras.unshift({
            id: this.company.productora,
            name: this.company.productora_name || "Productora",
          });
        }

        // Intereses del cliente (perfil propio).
        if (!this.isAdminFlow) {
          try {
            this.me = await fetchMe();
          } catch (e) {
            console.error("No se pudo cargar el perfil", e);
          }
          if (this.isClient) {
            this.genres = (await fetchGenres().catch(() => [])) || [];
            this.interests = {
              genreIds: (this.me?.interestGenres || []).map((g) => Number(g.id)),
              stageSurface: this.me?.stageSurface || "",
              budget: this.me?.budget ?? null,
              preferredSeason: this.me?.preferredSeason || "",
              ageRange: this.me?.ageRange || "",
            };
          }
        }
      } catch (e) {
        this.error = "No se pudo cargar la compañía.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = { ...this.company };
        payload.interest_ids = this.company.interest_ids || [];
        let savedId = this.company.id;
        if (this.create) {
          const created = await createCompany(payload);
          savedId = created?.id;
        } else {
          await saveCompany(this.company.id, payload, false);
        }
        // El selector "Intereses" de DATOS BÁSICOS edita los mismos datos
        // que la pestaña "Mis intereses", así que se guardan juntos.
        if (this.showInterests) {
          await saveMyInterests(this.interestsPayload());
        }
        // El logo va aparte: necesita el id de la compañía y sube a S3 como
        // multipart. Después de guardar, para que pise la URL del formulario.
        if (this.logoFile && savedId) {
          const { logo } = await uploadCompanyLogo(savedId, this.logoFile);
          this.company.logo = logo;
          this.logoFile = null;
          this.clearLogoObjectUrl();
        }
        if (this.isAdminFlow) {
          window.location.href = "/companias/";
          return;
        }
        this.success = "Datos guardados correctamente.";
        await this.load();
      } catch (e) {
        this.error = e.response?.data?.detail || "No se pudieron guardar los datos.";
        console.error(e);
        this.saving = false;
      }
    },
    async saveInterests() {
      this.savingInterests = true;
      this.error = "";
      this.success = "";
      try {
        await saveMyInterests(this.interestsPayload());
        this.success = "Intereses actualizados correctamente.";
      } catch (e) {
        this.error = e.response?.data?.detail || "No se pudieron actualizar tus intereses.";
        console.error(e);
      } finally {
        this.savingInterests = false;
      }
    },
    interestsPayload() {
      return {
        interest_genres: this.interests.genreIds,
        stage_surface: this.interests.stageSurface || "",
        budget: this.interests.budget === "" ? null : this.interests.budget,
        preferred_season: this.interests.preferredSeason || "",
        age_range: this.interests.ageRange || "",
      };
    },
    triggerLogoPicker() {
      this.$refs.logoInput?.click();
    },
    onLogoSelected(event) {
      const file = (event.target.files && event.target.files[0]) || null;
      this.clearLogoObjectUrl();
      this.logoFile = file;
      if (file) this.logoObjectUrl = URL.createObjectURL(file);
    },
    clearLogoObjectUrl() {
      if (this.logoObjectUrl) {
        URL.revokeObjectURL(this.logoObjectUrl);
        this.logoObjectUrl = "";
      }
    },
    toggleGenre(id, on) {
      const gid = Number(id);
      const set = new Set(this.interests.genreIds);
      if (on) set.add(gid);
      else set.delete(gid);
      this.interests.genreIds = Array.from(set);
    },
    addRep() {
      if (!Array.isArray(this.company.legal_representatives)) {
        this.company.legal_representatives = [];
      }
      this.company.legal_representatives.push({
        first_name: "",
        last_name: "",
        rut: "",
        phone: "",
        email: "",
        is_primary: false,
      });
    },
    removeRep(idx) {
      this.company.legal_representatives.splice(idx, 1);
    },
    cancel() {
      window.location.href = this.isAdminFlow ? "/companias/" : "/";
    },
  },
};
</script>

<style scoped>
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.obra-form-body {
  padding: 30px 34px 28px;
}

/* --- Layout de pestañas (sidebar + contenido) --- */
.profile-layout {
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 30px;
  align-items: start;
}

.profile-tabs {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.profile-tab {
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.profile-tab:hover {
  background: rgba(255, 255, 255, 0.06);
}

.profile-tab-active {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
}

.profile-content {
  min-width: 0;
}

.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 18px;
}

.obra-form-actions-center {
  justify-content: center;
}

.obra-cancel-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.obra-cancel-btn:hover {
  opacity: 0.72;
}

.obra-save-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obra-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.obra-save-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

/* Force the Vuetify fields to the white look of the mockup. */
.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}

.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input),
.obra-form-panel :deep(.v-field textarea),
.obra-form-panel :deep(.v-select__selection-text) {
  color: #1a1a1a !important;
}

.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}

/* Al flotar, el label queda a caballo entre el campo blanco y el panel oscuro;
   fondo oscuro tipo etiqueta con texto blanco para que siempre contraste. */
.obra-form-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Texto de ayuda (hint) legible sobre el panel oscuro. */
.obra-form-panel :deep(.v-messages),
.obra-form-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}

.obra-form-panel :deep(.v-field__append-inner .v-icon),
.obra-form-panel :deep(.v-field__clearable .v-icon) {
  color: #7a7a7a !important;
}

.obra-form-panel :deep(.v-chip) {
  background: #eceff1 !important;
  color: #1a1a1a !important;
}

/* The switch label sits on the dark panel, keep it light. */
.obra-form-panel :deep(.v-switch .v-label) {
  color: var(--color-theater-text) !important;
  opacity: 1;
}

/* --- Legal representatives ---------------------------------------------- */
.reps-section {
  margin-top: 10px;
}

.reps-title {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.reps-row {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 14px 4px;
  margin-bottom: 14px;
}

.reps-row-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 4px 8px;
}

.reps-remove {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #ff8a8a;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  transition: opacity 0.2s ease;
}

.reps-remove:hover {
  opacity: 0.8;
}

.reps-add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--color-theater-text);
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  transition: background 0.2s ease;
}

.reps-add:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* --- Mis intereses ------------------------------------------------------ */
.mi-heading {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0 0 14px;
}

.mi-heading-mt {
  margin-top: 26px;
}

.mi-genres {
  max-width: 520px;
}

.mi-genre-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.mi-genre-label {
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: var(--color-theater-text);
  text-transform: uppercase;
}

.mi-empty {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  padding: 8px 0;
}

/* --- Subida del logotipo ------------------------------------------------- */
.logo-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.logo-file-hidden {
  display: none;
}

.logo-preview {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  background: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex: 0 0 auto;
}

.logo-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.logo-upload-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.logo-file-name {
  font-size: 12px;
  color: var(--color-theater-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 45%;
}

@media (max-width: 760px) {
  .profile-layout {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .profile-tabs {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
